import React, { ReactNode, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { grey, red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputField, FormRadioGroup, InputField, InterestInputField, SelectField } from 'components/form';
import { FinanceProgramFormValues, MarkupMethods, MarkupType, Structures } from '../../../../../../types';
import { markupOptions, markupProposalOptions, styleFormRadioGroupProps } from '../../../../../../constants';
import { booleanOptions } from 'modules/financeProgram/constants';
import { SubsidyCapType } from '@trnsact/trnsact-shared-types';
import { deskingSelectors } from 'modules/desking/model';
import { useSelector } from 'react-redux';

type FieldsByConfigurationType = Record<
  Structures,
  { label: string; radioGroupField: ReactNode; valueField: ReactNode } | null
>;

export const StructureConfiguration = () => {
  const classes = useStyles();

  const isFinanceDetailsEditable = useSelector(deskingSelectors.selectFinanceDetailsEditableStatus);

  const { control, setValue, resetField } = useFormContext<FinanceProgramFormValues>();
  const financeQuote = useWatch({ control, name: 'financeQuote' });
  const equipment = useWatch({ control, name: 'equipment' });

  const [renderType, setRenderType] = useState<'buttons' | 'fields'>('buttons');

  useEffect(() => {
    setRenderType(financeQuote.structure !== Structures.None ? 'fields' : 'buttons');
  }, [financeQuote.structure, financeQuote.createProposalWithExistingProgram]);

  // If I don't have this, the form does not initialize the subsidyCapConfiguration field
  useEffect(() => {
    if (!financeQuote.subsidyCapConfiguration) {
      setValue('financeQuote.subsidyCapConfiguration', {
        subsidyCapType: SubsidyCapType.FixedAmount,
        lenderMarketRates: [{ rate: 0, term: 0 }],
        lenderName: '',
        maxSubsidyAmount: 0,
        netInvoicePercentage: 0,
        netInvoiceAmount: Number(equipment.totalWholesaleCost) ?? 0,
      });
    }
  }, []);

  const handleChangeMarkup = () => {
    setRenderType('fields');
    setValue('financeQuote.structure', Structures.Markup);
    setValue('financeQuote.markupMethod', MarkupMethods.MarkupPercentage);
  };

  const handleChangeSubsidy = () => {
    setRenderType('fields');
    setValue('financeQuote.structure', Structures.Subsidy);
  };

  const handleChangeRemove = () => {
    resetField('financeQuote.markupRate');
    resetField('financeQuote.subsidyType');
    resetField('financeQuote.markupMethod');

    setRenderType('buttons');
  };

  const markupFields: Record<MarkupMethods, ReactNode> = {
    [MarkupMethods.MarkupPercentage]: (
      <InterestInputField
        control={control}
        label="Dealer Markup (%)"
        name="financeQuote.markupRate"
        textFieldProps={{ fullWidth: true, disabled: !isFinanceDetailsEditable }}
      />
    ),
    [MarkupMethods.MarkupDollar]: (
      <CurrencyInputField
        control={control}
        label="Dealer Markup ($)"
        name="financeQuote.markupRate"
        textFieldProps={{ fullWidth: true, disabled: !isFinanceDetailsEditable }}
      />
    ),
    [MarkupMethods.CustomerRate]: (
      <InterestInputField
        control={control}
        label="Customer Rate (%)"
        name="financeQuote.markupRate"
        textFieldProps={{ fullWidth: true, disabled: !isFinanceDetailsEditable }}
      />
    ),
  };

  const subsidyValueInputsByType: Record<MarkupType, ReactNode> = {
    [MarkupType.Percentage]: (
      <>
        <Box className="subsidyCapConfiguration" sx={{ marginTop: 2 }}>
          {/* Row 1 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {/* <InterestInputField
              control={control}
              name="financeQuote.subsidyPercentage"
              label="Customer Subsidized Rate (%)"
              textFieldProps={{
                required: true,
                style: {
                  maxWidth: "100%",
                },
              }}
            /> */}
            <SelectField
              control={control}
              name="financeQuote.configureSubsidyCap"
              label="Configure Subsidy Cap"
              options={booleanOptions}
              defaultValue={false}
              sx={{ flex: 1 }}
              disabled={!isFinanceDetailsEditable}
            />
          </Box>

          {/* Row 2: Cap Type and Cap Value */}
          {financeQuote.configureSubsidyCap && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <SelectField
                control={control}
                name="financeQuote.subsidyCapConfiguration.subsidyCapType"
                label="Cap Type"
                options={[
                  { label: 'Fixed $ Amount', value: SubsidyCapType.FixedAmount },
                  { label: 'Dealer Net Invoice %', value: SubsidyCapType.NetInvoicePercentage },
                ]}
                defaultValue={SubsidyCapType.FixedAmount}
                sx={{ flex: 1 }}
                disabled={!isFinanceDetailsEditable}
              />
              {financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount ? (
                <CurrencyInputField
                  control={control}
                  label="Subsidy Cap Amount"
                  name="financeQuote.subsidyCapConfiguration.maxSubsidyAmount"
                  textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                />
              ) : financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage ? (
                <>
                  <CurrencyInputField
                    control={control}
                    label="Dealer Net Invoice Amount ($)"
                    name="financeQuote.subsidyCapConfiguration.netInvoiceAmount"
                    textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                  />
                  <InterestInputField
                    control={control}
                    label="Subsidy Cap Percentage"
                    name="financeQuote.subsidyCapConfiguration.netInvoicePercentage"
                    textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                  />
                </>
              ) : null}
            </Box>
          )}

          {/* Row 3: Lender Name and Lender Market Rate */}
          {financeQuote.configureSubsidyCap && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <InputField
                control={control}
                label="Lender Name"
                name="financeQuote.subsidyCapConfiguration.lenderName"
                inputProps={{ required: true, disabled: !isFinanceDetailsEditable }}
              />
              <InterestInputField
                control={control}
                required={true}
                label="Lender Market Rate"
                name="financeQuote.commonLenderMarketRate"
                textFieldProps={{ fullWidth: true, disabled: !isFinanceDetailsEditable }}
              />
            </Box>
          )}
        </Box>
      </>
    ),
    [MarkupType.Flat]: (
      <>
        <Box className="subsidyCapConfiguration" sx={{ marginTop: 2 }}>
          {/* Row 1 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {/* <CurrencyInputField
              control={control}
              label="Subsidized Customer Amount ($)"
              name="financeQuote.subsidyAmount"
              textFieldProps={{
                required: true,
                style: {
                  maxWidth: "100%",
                },
              }}
            /> */}
            <SelectField
              control={control}
              name="financeQuote.configureSubsidyCap"
              label="Configure Subsidy Cap"
              options={booleanOptions}
              defaultValue={false}
              sx={{ flex: 1 }}
              disabled={!isFinanceDetailsEditable}
            />
          </Box>

          {/* Row 2: Cap Type and Cap Value */}
          {financeQuote.configureSubsidyCap && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <SelectField
                control={control}
                name="financeQuote.subsidyCapConfiguration.subsidyCapType"
                label="Cap Type"
                options={[
                  { label: 'Fixed $ Amount', value: SubsidyCapType.FixedAmount },
                  { label: 'Dealer Net Invoice %', value: SubsidyCapType.NetInvoicePercentage },
                ]}
                defaultValue={SubsidyCapType.FixedAmount}
                sx={{ flex: 1 }}
                disabled={!isFinanceDetailsEditable}
              />
              {financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount ? (
                <CurrencyInputField
                  control={control}
                  label="Subsidy Cap Amount"
                  name="financeQuote.subsidyCapConfiguration.maxSubsidyAmount"
                  textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                />
              ) : financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage ? (
                <>
                  <CurrencyInputField
                    control={control}
                    label="Dealer Net Invoice Amount ($)"
                    name="financeQuote.subsidyCapConfiguration.netInvoiceAmount"
                    textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                  />
                  <InterestInputField
                    control={control}
                    label="Subsidy Cap Percentage"
                    name="financeQuote.subsidyCapConfiguration.netInvoicePercentage"
                    textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                  />
                </>
              ) : null}
            </Box>
          )}

          {/* Row 3: Lender Name and Lender Market Rate */}
          {financeQuote.configureSubsidyCap && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <InputField
                control={control}
                label="Lender Name"
                name="financeQuote.subsidyCapConfiguration.lenderName"
                inputProps={{ required: true, disabled: !isFinanceDetailsEditable }}
              />
              <InterestInputField
                control={control}
                required={true}
                label="Lender Market Rate"
                name="financeQuote.commonLenderMarketRate"
                textFieldProps={{ fullWidth: true, disabled: !isFinanceDetailsEditable }}
              />
            </Box>
          )}
        </Box>
      </>
    ),
  };

  const fieldsByConfigurationType: FieldsByConfigurationType = {
    [Structures.None]: null,
    [Structures.Markup]: {
      label: 'Set Markup',
      radioGroupField: (
        <Controller
          control={control}
          name="financeQuote.markupMethod"
          render={({ field }) => (
            <FormRadioGroup
              row
              options={markupOptions}
              {...styleFormRadioGroupProps}
              {...field}
              disabled={!isFinanceDetailsEditable}
            />
          )}
        />
      ),
      valueField: markupFields[financeQuote.markupMethod],
    },
    [Structures.Subsidy]: {
      label: 'Subsidy Configuration',
      radioGroupField: null,
      // (
      //   <Box className="row">
      //     <Controller
      //       control={control}
      //       name="financeQuote.subsidyType"
      //       render={({ field }) => (
      //         <FormRadioGroup row options={markupProposalOptions} {...styleFormRadioGroupProps} {...field} />
      //       )}
      //     />
      //   </Box>
      // ),
      valueField: (
        <>
          <Box className="subsidyCapConfiguration" sx={{ marginTop: 2 }}>
            {/* Row 1 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {/* <InterestInputField
                control={control}
                name="financeQuote.subsidyPercentage"
                label="Customer Subsidized Rate (%)"
                textFieldProps={{
                  required: true,
                  style: {
                    maxWidth: "100%",
                  },
                }}
              /> */}
              <SelectField
                control={control}
                name="financeQuote.configureSubsidyCap"
                label="Configure Subsidy Cap"
                options={booleanOptions}
                defaultValue={false}
                sx={{ flex: 1 }}
                disabled={!isFinanceDetailsEditable}
              />
              {financeQuote.configureSubsidyCap && (
                <InputField
                  control={control}
                  label="Lender Name"
                  name="financeQuote.subsidyCapConfiguration.lenderName"
                  inputProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                />
              )}
            </Box>

            {/* Row 2: Cap Type and Cap Value */}
            {financeQuote.configureSubsidyCap && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <SelectField
                  control={control}
                  name="financeQuote.subsidyCapConfiguration.subsidyCapType"
                  label="Cap Type"
                  options={[
                    { label: 'Fixed $ Amount', value: SubsidyCapType.FixedAmount },
                    { label: 'Dealer Net Invoice %', value: SubsidyCapType.NetInvoicePercentage },
                  ]}
                  defaultValue={SubsidyCapType.FixedAmount}
                  sx={{ flex: 1 }}
                  disabled={!isFinanceDetailsEditable}
                />
                {financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount ? (
                  <CurrencyInputField
                    control={control}
                    label="Subsidy Cap Amount"
                    name="financeQuote.subsidyCapConfiguration.maxSubsidyAmount"
                    textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                  />
                ) : financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage ? (
                  <>
                    <CurrencyInputField
                      control={control}
                      label="Dealer Net Invoice Amount ($)"
                      name="financeQuote.subsidyCapConfiguration.netInvoiceAmount"
                      textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                    />
                    <InterestInputField
                      control={control}
                      label="Subsidy Cap Percentage"
                      name="financeQuote.subsidyCapConfiguration.netInvoicePercentage"
                      textFieldProps={{ required: true, disabled: !isFinanceDetailsEditable }}
                    />
                  </>
                ) : null}
              </Box>
            )}

            {/* Row 3: Lender Name and Lender Market Rate */}
            {financeQuote.configureSubsidyCap && !financeQuote.specifyRate && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <InterestInputField
                  control={control}
                  required={true}
                  label="Lender Market Rate"
                  name="financeQuote.commonLenderMarketRate"
                  textFieldProps={{ fullWidth: true, disabled: !isFinanceDetailsEditable }}
                />
              </Box>
            )}
          </Box>
        </>
      ),
    },
  };

  const renderByType: Record<'buttons' | 'fields', ReactNode> = {
    buttons: (
      <Box className={classes.btnsContainer}>
        <Button variant="contained" size="small" onClick={handleChangeMarkup} disabled={!isFinanceDetailsEditable}>
          Configure Markup
        </Button>
        <Button variant="contained" size="small" onClick={handleChangeSubsidy} disabled={!isFinanceDetailsEditable}>
          Configure Subsidy
        </Button>
      </Box>
    ),
    fields: (
      <Box className={classes.fieldsContainer}>
        <Box className="radioSection">
          <Box className="radioSectionField">
            <Typography component="span" variant="subtitle2">
              {fieldsByConfigurationType[financeQuote.structure]?.label}
            </Typography>

            {fieldsByConfigurationType[financeQuote.structure]?.radioGroupField}
          </Box>

          <Button variant="text" size="small" style={{ color: red['500'] }} onClick={handleChangeRemove}>
            Remove
          </Button>
        </Box>

        {fieldsByConfigurationType[financeQuote.structure]?.valueField}
      </Box>
    ),
  };

  return <>{renderByType[renderType]}</>;
};

const useStyles = makeStyles({
  btnsContainer: {
    gap: '1rem',
    display: 'flex',
    alignItems: 'center',
  },
  fieldsContainer: {
    padding: '0.75rem',
    borderRadius: '2px',
    backgroundColor: grey['100'],

    '& .radioSection': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',

      '& .radioSectionField': {
        gap: '1rem',
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
  formRow: {
    gap: '24px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    paddingRight: '4px',

    '& .subsidyCapConfiguration': {
      gap: '16px',
      padding: '8px',
    },
  },
});
